import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/16/solid';
import { useNavigate } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useEventListener } from 'usehooks-ts';
import { ASSET_ROUTE_URL } from '../../../constants/routes';
import { useFavoriteProducts } from '../../../hooks/useFavoriteProducts';
import { useModalState } from '../../../hooks/useModalState';
import { useProductPricesSub } from '../../../hooks/useProductPricesSub';
import { useProducts } from '../../../hooks/useProducts';
import { useScrolledToEnd } from '../../../hooks/useScrolledToEnd';
import { formatDollar, formatNumber, formatPercentage } from '../../../lib/formatters';
import { multiply } from '../../../lib/math';
import { Badge } from '../../catalyst-ui-kit/badge';
import { Button } from '../../catalyst-ui-kit/button';
import { Divider } from '../../catalyst-ui-kit/divider';
import { Input } from '../../catalyst-ui-kit/input';
import { ColoredMetric } from '../../ColoredMetric/ColoredMetric';
import { FavoriteButton } from '../../FavoriteButton/FavoriteButton';
import { InlineSelect, InlineSelectOption } from '../../InlineSelect/InlineSelect';
import { Kbd } from '../../Kbd/Kbd';
import { ResponsiveModal } from '../../ResponsiveModal/ResponsiveModal';
import { MarketStateTable, MarketTableData } from '../../Table/MarketsTable';

// Component that adds negative left margin for negative values to maintain column alignment
const AlignedColoredMetric: React.FC<{
  value: bigint | undefined;
  formatFn: (value: bigint | undefined) => string;
}> = ({ value, formatFn }) => {
  const isNegative = value !== undefined && value < 0n;
  return (
    <div className={isNegative ? '-ml-2' : ''}>
      <ColoredMetric value={value} formatFn={formatFn} />
    </div>
  );
};

type MarketsModalUiProps = {
  tableData: MarketTableData[];
  isPending: boolean;
  isFetching: boolean;
  toggleProductFavorite: (productId: string) => void;
  onRowClick: ({ routingTicker }: { routingTicker: string }) => void;
};

const filterButtons = [
  { value: 'all', label: 'All markets' },
  { value: 'favorite', label: 'Favorites' },
] as const;

export const MarketsModalUi: React.FC<MarketsModalUiProps> = ({
  tableData,
  isFetching,
  isPending,
  toggleProductFavorite,
  onRowClick,
}) => {
  const isOpen = useModalState((state) => state.marketsModal === 'open');
  const setMarketsModal = useModalState((state) => state.setMarketsModal);

  const tableRef = useRef<HTMLDivElement>(null);

  const [selectedFilter, setSelectedFilter] = useState<
    InlineSelectOption<(typeof filterButtons)[number]['value']> | undefined
  >(filterButtons[0]);
  const [search, setSearch] = useState('');
  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(-1);
  const [isUsingKeyboard, setIsUsingKeyboard] = useState(false);

  const isAtBottom = useScrolledToEnd(tableRef);

  ////// USE EFFECTS //////
  // Reset search, filter, and row selection when modal opens
  useEffect(() => {
    setSearch('');
    setSelectedFilter(filterButtons[0]);
    setSelectedRowIndex(-1);
  }, [isOpen]);

  // Reset row selection when filter or search changes
  useEffect(() => {
    setSelectedRowIndex(0);
  }, [search, selectedFilter]);

  // Custom scroll with offset when selected row changes
  useEffect(() => {
    if (tableRef.current) {
      const container = tableRef.current;
      const rows = container.querySelectorAll('tbody tr');

      if (rows[selectedRowIndex]) {
        const selectedRow = rows[selectedRowIndex];
        const containerRect = container.getBoundingClientRect();
        const rowRect = selectedRow.getBoundingClientRect();

        const containerOffset = 10; // offset to use check if the row is above or below the viewport
        const scrollOffset = 20; // offset to use to scroll the row into view

        // Calculate if row is visible in the viewport
        const isRowAboveViewport = rowRect.top < containerRect.top + containerOffset;
        const isRowBelowViewport = rowRect.bottom > containerRect.bottom - containerOffset;

        if (isRowAboveViewport) {
          // Scrolling up - add offset above the row
          const offset = rowRect.top - containerRect.top - scrollOffset;
          const scrollTop = container.scrollTop + offset;
          container.scrollTo({ top: scrollTop, behavior: 'smooth' });
        } else if (isRowBelowViewport) {
          // Scrolling down - add offset below the row
          const offset = rowRect.bottom - containerRect.bottom + scrollOffset;
          const scrollTop = container.scrollTop + offset;
          container.scrollTo({ top: scrollTop, behavior: 'smooth' });
        }
      }
    }
  }, [selectedRowIndex]);

  // Add a mouseMove handler to detect mouse movement
  useEffect(() => {
    const handleMouseMove = () => {
      if (isUsingKeyboard) {
        setIsUsingKeyboard(false);
      }
    };
    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isUsingKeyboard]);

  const withFavoritesFirst = <T extends MarketTableData>(
    originalSortFn: (rowA: Row<T>, rowB: Row<T>) => number
  ) => {
    return (rowA: Row<T>, rowB: Row<T>): number => {
      // Always prioritize favorites regardless of sort direction
      if (rowA.original.isFavorite !== rowB.original.isFavorite) {
        return rowA.original.isFavorite ? -1 : 1;
      }

      // If both rows have the same favorite status, use the original sort function
      return originalSortFn(rowA, rowB);
    };
  };

  const sortBigInt = (a?: bigint, b?: bigint) => {
    return (a || -1n) > (b || -1n) ? 1 : -1;
  };

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<MarketTableData>();
    const cols = [
      columnHelper.accessor(
        ({ market, maxLeverage, isFavorite, productId }) => ({
          market,
          maxLeverage,
          isFavorite,
          productId,
        }),
        {
          id: 'market_info',
          header: 'Market',
          meta: { cellContainerClassName: 'w-0 min-w-[150px] pl-0' },
          sortingFn: withFavoritesFirst((rowA, rowB) => {
            const marketA = rowA.original.market.toLowerCase();
            const marketB = rowB.original.market.toLowerCase();
            return marketA.localeCompare(marketB);
          }),
          cell: (info) => {
            const { market, maxLeverage, isFavorite, productId } = info.getValue();
            return (
              <div className={'flex items-center gap-4'}>
                <FavoriteButton
                  title={isFavorite ? `Unfavorite ${market}` : `Favorite ${market}`}
                  isFavorite={isFavorite}
                  onToggle={() => toggleProductFavorite(productId)}
                  className="mx-0.5 size-4"
                />
                <div className="text-sm font-medium text-contentPrimary">{market}</div>
                <Badge>{maxLeverage}x</Badge>
              </div>
            );
          },
        }
      ),
      columnHelper.accessor('price', {
        id: 'oracle_price',
        header: () => 'Price',
        cell: (info) => formatNumber(info.getValue()?.oraclePrice),
        meta: { cellHeaderClassname: 'text-nowrap' },
        sortingFn: withFavoritesFirst((rowA, rowB) => {
          return sortBigInt(rowA.original.price?.oraclePrice, rowB.original.price?.oraclePrice);
        }),
      }),
      columnHelper.accessor('price', {
        id: '24h_change',
        header: () => '24h Change',
        cell: (info) => (
          <AlignedColoredMetric
            value={info.getValue()?.change24hrPercent}
            formatFn={formatPercentage}
          />
        ),
        meta: { cellHeaderClassname: 'text-nowrap' },
        sortingFn: withFavoritesFirst((rowA, rowB) => {
          return sortBigInt(
            rowA.original.price?.change24hrPercent,
            rowB.original.price?.change24hrPercent
          );
        }),
      }),
      columnHelper.accessor('volume24h', {
        header: '24h Volume',
        cell: (info) => formatDollar(info.getValue()),
        meta: { cellHeaderClassname: 'text-nowrap' },
        sortingFn: withFavoritesFirst((rowA, rowB) => {
          return sortBigInt(rowA.original.volume24h, rowB.original.volume24h);
        }),
      }),

      columnHelper.accessor('fundingRate1h', {
        header: 'Funding Rate',
        cell: (info) => (
          <AlignedColoredMetric
            value={info.getValue()}
            formatFn={(fundingRate1h) =>
              formatPercentage(fundingRate1h, {
                maximumFractionDigits: 4,
                minimumFractionDigits: 4,
              })
            }
          />
        ),
        meta: { cellHeaderClassname: 'text-nowrap' },
        sortingFn: withFavoritesFirst((rowA, rowB) => {
          return sortBigInt(rowA.original.fundingRate1h, rowB.original.fundingRate1h);
        }),
      }),
    ];
    return cols;
  }, [toggleProductFavorite]);

  const filteredTableData = useMemo(() => {
    let result = tableData;

    if (selectedFilter?.value === 'favorite') {
      result = tableData.filter((row) => row.isFavorite);
    }
    if (search) {
      const lowerSearch = search.toLowerCase();
      result = result.filter((row) => row.market.toLowerCase().includes(lowerSearch));
    }

    return result;
  }, [search, selectedFilter?.value, tableData]);

  const table = useReactTable({
    columns,
    data: filteredTableData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      sorting: [
        {
          id: 'volume24h',
          desc: true,
        },
      ],
    },
  });

  const originalRows = table.getSortedRowModel().rows;

  const sortedRows = originalRows.sort((a, b) => {
    const aIsFavorite = a.original.isFavorite;
    const bIsFavorite = b.original.isFavorite;
    if (aIsFavorite && !bIsFavorite) return -1;
    if (!aIsFavorite && bIsFavorite) return 1;
    return 0;
  });

  const handleKeyDownInternal = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          setIsUsingKeyboard(true); // Flag that we're using keyboard
          setSelectedRowIndex((prev) => {
            const newIndex = prev <= 0 ? sortedRows.length - 1 : prev - 1;
            return newIndex;
          });
          break;
        case 'ArrowDown':
          event.preventDefault();
          setIsUsingKeyboard(true); // Flag that we're using keyboard
          setSelectedRowIndex((prev) => {
            const newIndex = prev === sortedRows.length - 1 ? 0 : prev + 1;
            return newIndex;
          });
          break;
        case 'Enter':
          event.preventDefault();
          if (
            selectedRowIndex >= 0 &&
            selectedRowIndex < sortedRows.length &&
            sortedRows[selectedRowIndex]
          ) {
            onRowClick({ routingTicker: sortedRows[selectedRowIndex].original.routingTicker });
            setMarketsModal('closed');
          }
          break;
        case 'Escape':
          event.preventDefault();
          setMarketsModal('closed');
          break;
      }
    },
    [isOpen, onRowClick, setMarketsModal, selectedRowIndex, sortedRows]
  );
  useEventListener('keydown', handleKeyDownInternal);

  return (
    <ResponsiveModal
      panelClassName="h-[70vh] max-w-[710px]"
      childrenContainerClassName="h-full overflow-hidden p-0"
      onClose={() => setMarketsModal('closed')}
      open={isOpen}
      hideCloseButton
    >
      <div className="flex h-full flex-col overflow-hidden">
        <div className="flex shrink-0 flex-col gap-4 sm:p-4 ">
          <Input
            autoFocus
            className="text-contentSecondary"
            leftElement={<MagnifyingGlassIcon className="size-4 text-contentSecondary" />}
            rightElement={
              search && (
                <Button
                  text
                  color="secondary"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                >
                  <XMarkIcon />
                </Button>
              )
            }
            onChange={(e) => setSearch(e.target.value)}
            value={search}
            placeholder="Search Markets"
          />
          <InlineSelect
            onChange={(val) => {
              setSelectedFilter(val);
            }}
            options={filterButtons}
            selected={selectedFilter?.value}
          />
        </div>
        <div className="relative min-h-0 flex-1 sm:px-4">
          <div className="h-full overflow-auto pb-2">
            <MarketStateTable
              ref={tableRef}
              requireConnected={false}
              table={table}
              selectedRowIndex={selectedRowIndex}
              onRowClick={(data) => {
                onRowClick(data as MarketTableData);
                setMarketsModal('closed');
              }}
              noDataText={
                selectedFilter?.value === 'favorite'
                  ? 'No markets marked as favorite.'
                  : 'No results found.'
              }
              isPending={isPending}
              isFetching={isFetching}
              setSelectedRowIndex={setSelectedRowIndex}
              isUsingKeyboard={isUsingKeyboard}
              onRowFocus={(index) => {
                setIsUsingKeyboard(true);
                setSelectedRowIndex(index);
              }}
            />
          </div>
          <div
            className={twMerge(
              'pointer-events-none absolute inset-x-0 bottom-0 z-10 h-[48px] bg-gradient-to-t from-backgroundPrimary to-transparent transition-opacity',
              isAtBottom ? 'opacity-0' : 'opacity-100'
            )}
          />
        </div>
        <Divider className="shrink-0 max-sm:hidden" />
        <div className="flex shrink-0 justify-between p-4 max-sm:hidden">
          <div className="flex items-center gap-3 text-xs text-contentSecondary">
            <div className="flex items-center gap-1">
              <Kbd variant="text">↑</Kbd>
              <Kbd variant="text">↓</Kbd>
            </div>
            Navigate
          </div>
          <div className="flex items-center gap-3 text-xs text-contentSecondary">
            <Kbd variant="text">Enter</Kbd>
            Open market
          </div>
          <div className="flex items-center gap-3 text-xs text-contentSecondary">
            <Kbd variant="text">ESC</Kbd>
            Close modal
          </div>
        </div>
      </div>
    </ResponsiveModal>
  );
};

export const MarketsModal = () => {
  const { data: products = [], isFetching, isPending } = useProducts();
  const navigate = useNavigate({ from: ASSET_ROUTE_URL });
  const setMarketsModal = useModalState((state) => state.setMarketsModal);
  const open = useModalState((state) => state.marketsModal === 'open');
  const { favoriteProducts, toggleProductFavorite } = useFavoriteProducts();
  const { data: pricesByProductId } = useProductPricesSub();

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setMarketsModal(!open ? 'open' : 'closed');
      }
    },
    [open, setMarketsModal]
  );
  useEventListener('keydown', handleKeyDown);

  const tableData = useMemo(
    () =>
      products.map((product) => {
        const price = pricesByProductId[product.id];
        return {
          productId: product.id,
          market: product.displayTicker,
          routingTicker: product.ticker,
          volume24h: price?.oraclePrice
            ? multiply(product.volume24h, price.oraclePrice)
            : undefined,
          fundingRate1h: product.fundingRate1h,
          baseTokenName: product.baseTokenName,
          maxLeverage: product.maxLeverage,
          isFavorite: Boolean(favoriteProducts[product.id]),
          price,
        };
      }),
    [products, favoriteProducts, pricesByProductId]
  );

  return (
    <MarketsModalUi
      tableData={tableData}
      isFetching={isFetching}
      isPending={isPending}
      toggleProductFavorite={toggleProductFavorite}
      onRowClick={({ routingTicker }) => {
        void navigate({
          to: ASSET_ROUTE_URL,
          params: { asset: routingTicker },
          search: (prev) => prev,
        });
      }}
    />
  );
};
